/**
 * Управление закреплёнными чатами через localStorage.
 *
 * Бэкенд-эндпоинт POST /api/chats/{chatId}/toggle-pin уже реализован,
 * но пока не развёрнут на проде. Эти функции обеспечивают работу
 * пинов на клиенте без зависимости от сервера.
 *
 * Когда бэкенд будет обновлён, можно будет переключиться на серверный
 * вариант — структура данных (isPinned в ChatResponse) уже готова.
 */

const STORAGE_KEY = 'hrnb:pinned-chats';

/**
 * Получить множество ID закреплённых чатов из localStorage.
 * @returns {Set<string>}
 */
export function getPinnedChatIds() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return new Set();
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? new Set(arr) : new Set();
    } catch {
        return new Set();
    }
}

/**
 * Проверить, закреплён ли чат.
 * @param {string} chatId - ID чата
 * @returns {boolean}
 */
export function isChatPinned(chatId) {
    return getPinnedChatIds().has(chatId);
}

/**
 * Переключить статус закрепления чата.
 * @param {string} chatId - ID чата
 * @returns {boolean} Новое состояние (true = закреплён)
 */
export function toggleChatPin(chatId) {
    const ids = getPinnedChatIds();
    let nowPinned;
    if (ids.has(chatId)) {
        ids.delete(chatId);
        nowPinned = false;
    } else {
        ids.add(chatId);
        nowPinned = true;
    }
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(ids)));
    } catch { /* localStorage недоступен */ }
    return nowPinned;
}

/**
 * Применить пины к списку чатов — добавляет isPinned каждому чату.
 * Если чат уже имеет isPinned с сервера — приоритет за сервером.
 * @param {Array} chats - Список чатов
 * @returns {Array} Тот же список с добавленным полем isPinned
 */
export function applyPinnedState(chats) {
    const pinnedIds = getPinnedChatIds();
    return chats.map(chat => ({
        ...chat,
        isPinned: chat.isPinned ?? pinnedIds.has(chat.id)
    }));
}
